import { FormEvent, useState } from 'react'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Modal, Input } from 'antd'
import { FormComponentProps } from '@ant-design/compatible/lib/form/Form'
import { useDispatch, useSelector } from 'react-redux'

import Box from '~/core/Box'
import Text from '~/core/Text'
import Image from '~/core/Image'
import Button from '~/core/Button'
import { getUserIdentity, SUCCESS } from '~/redux-modules/identity'
import { setMfaStatus } from '~/redux-modules/auth'
import { onlyNumsWithLength } from '~/lib/normalize'
import { trackIdentify } from '~/lib/analytics/actions/identify'
import { hasErrors } from '~/lib/formHelpers'
import {
    ENABLE_TITLE,
    DISABLE_TITLE,
    ENABLE_DESCRIPTION,
    DISABLE_DESCRIPTION,
    ENABLE_PLACEHOLDER,
    DISABLE_PLACEHOLDER,
    ENABLE_MFA_RULE,
    PASSWORD,
    VERIFICATION_CODE,
    VERIFICATION_CODE_LENGTH,
} from '../constants'
import {
    useDisable2FaMyAccountMutation,
    useVerify2FaMyAccountPageMutation,
} from '~/gql/graphql'

import { handleDisable2FA, handleVerify2FA } from './logic'
import { PASSWORD_RULES } from './PasswordForm'
import MfaErrorModal from './MfaErrorModal'

const PLACEMENT = 'mfa-modal-account-settings'

interface MfaModalPropsType extends FormComponentProps {
    id: number
    qrCode?: string | null
    mfaEnabled?: boolean
    visible: boolean
    closeModal: () => void
    setMfaEnabled: (mfaEnabled: boolean) => void
}

function MfaModal(props: MfaModalPropsType) {
    const {
        id,
        qrCode,
        mfaEnabled,
        visible,
        closeModal,
        setMfaEnabled,
        form,
        form: { getFieldDecorator, getFieldsError, getFieldValue },
    } = props

    const [loading, setLoading] = useState<boolean>(false)
    const [verify2FA] = useVerify2FaMyAccountPageMutation()
    const [disable2FA] = useDisable2FaMyAccountMutation()
    const dispatch = useDispatch()
    let identity = useSelector(getUserIdentity)

    const onCancel = () => {
        setMfaEnabled(!mfaEnabled)
        closeModal()
    }

    const updateMfaStatus = (mfaStatus: boolean) => {
        identity = {
            ...identity,
            mfaEnabled,
        }
        const action = { type: SUCCESS, identity }

        dispatch(trackIdentify(action, id, identity))
        dispatch(setMfaStatus(mfaStatus))
    }

    if (mfaEnabled && !qrCode) {
        return <MfaErrorModal visible onCancel={onCancel} />
    }

    const title = (
        <Text color="blackAlpha65">
            {mfaEnabled ? ENABLE_TITLE : DISABLE_TITLE}
        </Text>
    )
    const description = mfaEnabled ? ENABLE_DESCRIPTION : DISABLE_DESCRIPTION
    const inputPlaceholder = mfaEnabled
        ? ENABLE_PLACEHOLDER
        : DISABLE_PLACEHOLDER
    const isButtonDisabled =
        loading ||
        hasErrors(getFieldsError()) ||
        !getFieldValue(mfaEnabled ? VERIFICATION_CODE : PASSWORD)
    const handleSubmit = (evt: Event | FormEvent) => {
        evt.preventDefault()
        if (mfaEnabled) {
            handleVerify2FA({
                id,
                form,
                setLoading,
                closeModal,
                verify2FA,
                updateMfaStatus,
            })
        } else {
            handleDisable2FA({
                id,
                form,
                setLoading,
                closeModal,
                disable2FA,
                updateMfaStatus,
            })
        }
    }

    return (
        <Modal
            visible={visible}
            title={title}
            closable
            onCancel={onCancel}
            footer={
                <Box justifyContent="end">
                    <Button
                        onClick={onCancel}
                        name="mfa-cancel"
                        placement={PLACEMENT}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        loading={loading}
                        disabled={isButtonDisabled}
                        onClick={handleSubmit}
                        name="mfa-enable-disable"
                        placement={PLACEMENT}
                    >
                        {mfaEnabled ? 'Enable' : 'Disable'}
                    </Button>
                </Box>
            }
            width={579}
            centered
        >
            <Box direction="col">
                {description}
                {mfaEnabled && (
                    <Box
                        width="163px"
                        height="175px"
                        backgroundColor="blackAlpha25"
                        margin="24px auto 8px auto"
                    >
                        <Image src={qrCode} />
                    </Box>
                )}
                {mfaEnabled && 'Verification code'}
                <Form onSubmit={handleSubmit}>
                    <Form.Item
                        required
                        style={{
                            width: '300px',
                            marginTop: !mfaEnabled ? '24px' : '0px',
                        }}
                        hasFeedback
                    >
                        {getFieldDecorator(
                            mfaEnabled ? VERIFICATION_CODE : PASSWORD,
                            {
                                rules: mfaEnabled
                                    ? ENABLE_MFA_RULE
                                    : PASSWORD_RULES,
                                normalize: value =>
                                    mfaEnabled
                                        ? onlyNumsWithLength(
                                              value,
                                              VERIFICATION_CODE_LENGTH,
                                          )
                                        : value,
                            },
                        )(
                            mfaEnabled ? (
                                <Input
                                    data-test-target={VERIFICATION_CODE}
                                    type="number"
                                    autoComplete="off"
                                    placeholder={inputPlaceholder}
                                />
                            ) : (
                                <Input.Password
                                    data-test-target={PASSWORD}
                                    placeholder={inputPlaceholder}
                                />
                            ),
                        )}
                    </Form.Item>
                </Form>
            </Box>
        </Modal>
    )
}

export default Form.create<MfaModalPropsType>()(MfaModal)
